import Component from '../core/Component'
import EventBus from '../core/EventBus'
import ScrollContainer from './ScrollContainer'
import { getScroll, enableScrolling, disableScrolling } from '../services/Scroll'
import support from '../utils/BrowserSupport'
import keycode from 'keycode'
import { removeComponents, loadComponents } from '../core/componentUtils'
import enquire from 'enquire.js'
import { queries, CMS } from '../core/config'
import anime from 'animejs'
import { scrollToElement } from '../services/Scroll'

export const defaults = {
    scroll: true,
    id: '',
}

export const STATES = {
    ACTIVE: 'is-active',
    VISIBLE: 'is-visible',
    LOADING: 'is-loading',
    FULLSCREEN: 'is-fullscreen',
    OVERLAY_VISIBLE: 'is-overlay-visible',
}

export default class Modal extends Component {
    constructor(element, options = {}) {
        super(element, {
            dispose: false,
            url: null,
            openOnInit: false,
            className: '',
            fullscreen: false,
            netteCloseOn: true,
            autoOpen: false,
            scroll: false,
            media: 'mediumUp',
            filterResponse: function (html) {
                return { content: html }
            },
        })

        this.ref = {
            background: null,
            placeholder: null,
            layer: null,
            inner: null,
            content: null,
            closeButtons: [],
        }

        this.options = options

        if (this.options.className) {
            this.element.classList.add(this.options.className)
        }

        if (this.options.fullscreen) {
            this.element.classList.add(STATES.FULLSCREEN)
        }

        if (this.options.content) {
            this.setContent(this.options.content)
        }

        this.request = null
        this.isActive = false
        this.isMobile = false

        if (this.options.id && !this.element.id) {
            this.element.id = this.options.id
        }

        this.enquireHandler = {
            attach: () => {
                this.isMobile = true
            },
            detach: () => {
                this.isMobile = false
            },
        }
    }

    prepare() {
        this.pageContent = document.getElementById('page-content')
        this.ref.closeButtons.forEach((element) =>
            element.addEventListener('click', this.handleCloseClick),
        )
        // this.ref.layer.addEventListener(support.transitionEnd, () => this.handleTransitionEnd(this.element, event))
        this.element.addEventListener(support.transitionEnd, () =>
            this.handleTransitionEnd(this.element, event),
        )

        this.element.addEventListener('click', this.handleOuterClick)

        enquire.register(queries.smallWideMax, this.enquireHandler)

        if (this.options.scroll && this.ref.inner) {
            this.scrollContainer = new ScrollContainer(this.ref.inner, {
                media: 'mediumUp',
            })

            if (this.scrollContainer) {
                this.scrollContainer.prepare()

                if (this.scrollContainer.scrollbar) {
                    this.scrollContainer.scrollbar.scrollTo(0, 0)
                }
            }
        }

        if (this.options.autoOpen && this.options.url) {
            this.load()
        } else if (this.options.openOnInit) {
            this.open()
        }
    }

    destroy() {
        document.removeEventListener('keydown', this.handleKeyDown)
        if (this.request) {
            this.request.abort()
        }

        enquire.register(queries.mediumUp, this.enquireHandler)

        this.scrollContainer?.destroy()

        EventBus.off('nette:event', this.handleNetteEvent)
    }

    handleTransitionEnd = (element, event) => {
        if (
            element === event.target &&
            !this.is(STATES.ACTIVE) &&
            !this.is(STATES.OVERLAY_VISIBLE)
        ) {
            if (!this.is(STATES.VISIBLE)) {
                return
            }

            this.element.classList.remove(STATES.VISIBLE)

            if (this.options.dispose) {
                removeComponents(this.ref.content)
                this.destroy()
                this.element.parentNode.removeChild(this.element)
                this.element = null
            }
        }
    }

    handleCloseClick = (event) => {
        event.preventDefault()
        this.close()
    }

    handleOuterClick = (event) => {
        // Close on click outside
        if (!this.ref.content.contains(event.target) && !event.target.closest('button')) {
            this.close()
        }
    }

    handleKeyDown = (event) => {
        if (keycode(event.keyCode) === 'esc') {
            this.close()
        }
    }

    handleNetteEvent = (data) => {
        const closeOn = JSON.stringify(this.options.netteCloseOn)
        const action = JSON.stringify(data)

        const that = this

        if (closeOn && action ) {
            setTimeout(() => {
                that.close()
            }, 3000)
        }
    }

    handleScrollTo = (id) => {
        if (!this.element.querySelector('#' + id)) {
            this.close()
        }
    }

    async _open(source = null) {
        if (source && !this.isMobile) {
            const sourceBox = source.getBoundingClientRect()
            const elementBox = this.ref.inner.getBoundingClientRect()

            anime.set(this.ref.placeholder, {
                width: `${sourceBox.width}px`,
                height: `${sourceBox.height}px`,
                top: `${sourceBox.top}px`,
                left: `${sourceBox.left}px`,
                // opacity: 0
            })

            await anime({
                targets: this.ref.placeholder,
                opacity: 1,
                duration: 100,
            }).finished

            await anime({
                targets: this.ref.placeholder,
                width: elementBox.width,
                height: Math.max(elementBox.height, window.innerHeight) - elementBox.top,
                top: elementBox.top,
                left: elementBox.left,
                duration: 500,
                easing: 'cubicBezier(.36,.12,.15,.94)',
            }).finished
        }

        this.element.classList.add(STATES.ACTIVE)
    }

    async open(source = null) {
        this.isActive = true
        disableScrolling()

        const eventData = {
            id: this.element.id || this.options.id,
            url: this.options.url,
        }

        if (this.options.preload) {
            this.load(true)
        }

        this.element.classList.add(STATES.VISIBLE)
        this.element.offsetWidth
        this.element.classList.add(STATES.OVERLAY_VISIBLE)

        EventBus.emit('modal:beforeopen', eventData)

        await this._open(source)

        document.addEventListener('keydown', this.handleKeyDown)

        this.emit('open', eventData)

        EventBus.emit('modal:open', eventData)

        if (this.options.url && !this.options.autoOpen && !this.options.preload) {
            this.load()
        }

        if (this.options.netteCloseOn) {
            EventBus.on('nette:event', this.handleNetteEvent)
        }
        EventBus.on('scrollTo:scroll', this.handleScrollTo)
    }

    close() {
        if (CMS) {
            return
        }

        if (!this.isActive) {
            return
        }

        this.isActive = false

        enableScrolling()
        this.element.classList.remove(STATES.ACTIVE)
        this.element.classList.remove(STATES.OVERLAY_VISIBLE)
        document.removeEventListener('keydown', this.handleKeyDown)

        this.emit('close', {
            id: this.element.id || this.options.id,
            url: this.options.url,
        })

        EventBus.emit('modal:close', {
            id: this.element.id || this.options.id,
            url: this.options.url,
        })

        EventBus.on('scrollTo:scroll', this.handleScrollTo)

        if (this.options.netteCloseOn) {
            EventBus.off('nette:event', this.handleNetteEvent)
        }
        history.pushState({}, null, '/');
    }

    load(preloading = false) {
        this.element.classList.add(STATES.LOADING)
        $.ajax({
            url: this.options.url,
            dataType: 'HTML',
            success: (response) => {
                this.setContent(this.options.filterResponse(response))
            },
            complete: () => {
                this.element.classList.remove(STATES.LOADING)

                if (this.options.autoOpen && !preloading) {
                    this.open()
                }
            },
        })
    }

    setContent({ content }) {
        removeComponents(this.ref.content)
        this.ref.content.innerHTML = content
        loadComponents(this.ref.content)
        $.nette.load()
    }
}

const template = `
    <div class="Modal-background" data-ref="background"></div>
    <div class="Modal-placeholder" data-ref="placeholder"></div>
    <div class="Modal-layer" data-ref="layer">
        <div class="Modal-inner">
            <div class="Modal-body" data-ref="inner">
                <div class="Modal-content" data-ref="content">
                </div>
            </div>
            <button type="button" class="Modal-close" data-ref="closeButton">Zavřít</button>
        </div>
    </div>
`

export function createModal(options) {
    const element = document.createElement('div')
    element.classList.add('Modal')
    element.innerHTML = template
    document.body.appendChild(element)

    const modal = new Modal(element, {
        dispose: true,
        ...options,
        id: 'dynamic-modal-' + Date.now(),
    })
    modal.prepare()

    return modal
}
